import * as React from "react";
import { homeLink, request } from "../../../services/cpln";
import { ChevronDown, ChevronUp } from "react-feather";
import { ExternalLink } from "../../../newcomponents/table/components/ExternalLink";
import { SyntaxHighlight } from "../../../components/syntaxHighlight/SyntaxHighlight";
import { DOCS_URL } from "../../../envVariables";
import { CopyButtonWithApproval } from "../../domain/create/copyButtonWithApproval";
import { CloudAccount } from "../../../schema/types/cloudaccount/entity";

interface Props {
  cloudAccount: CloudAccount;
}
export const CloudaccountGcpConfiguration: React.FC<Props> = ({ cloudAccount }) => {
  const [continueWith, setContinueWith] = React.useState("none");
  const [instructions, setInstructions] = React.useState<any>(null);

  React.useEffect(() => {
    request({ url: `${homeLink("cloudaccount")}/-instructions/gcp` }).then((res: any) => setInstructions(res.data));
  }, []);

  function onConsole() {
    if (continueWith === "console") {
      setContinueWith("none");
    } else {
      setContinueWith("console");
    }
  }

  function onCPLN() {
    if (continueWith === "cpln") {
      setContinueWith("none");
    } else {
      setContinueWith("cpln");
    }
  }

  const projectId = (cloudAccount.data as any)?.projectId || "<gcpProjectId>";

  if (!instructions || !instructions?.data) return null;

  return (
    <>
      <div className="my-4">
        <button type={"button"} className="color-link focus font-medium" onClick={onConsole}>
          Configure the cloud account using the GCP Console
          {continueWith === "console" ? (
            <ChevronUp className="ml-1 inline-block" size={16} style={{ transform: "translateY(2px)" }} />
          ) : (
            <ChevronDown className="ml-1 inline-block" size={16} style={{ transform: "translateY(2px)" }} />
          )}
        </button>
      </div>
      {continueWith === "console" ? (
        <ul className="pl-8">
          <li className="list-decimal mb-6 ml-4">
            Sign into the <ExternalLink url="https://console.cloud.google.com" label="GCP Console" disableCopy />.
          </li>
          <li className="list-decimal mb-6 ml-4">
            Navigate to the{" "}
            <ExternalLink url="https://console.cloud.google.com/iam-admin/iam" label="IAM Console" disableCopy />.
          </li>
          <li className="list-decimal mb-6 ml-4">
            Click <span className="font-semibold">Grant Policy</span> to add a principal.
          </li>
          <li className="list-decimal mb-6 ml-4">
            Input <span className="font-semibold mr-2">{instructions.data.bridgeServiceAccount}</span>
            <CopyButtonWithApproval className="mr-2" value={instructions.data.bridgeServiceAccount} />
            under <span className="font-semibold">New principals</span>.
          </li>
          <li className="list-decimal mb-1 ml-4">Assign the roles:</li>
          <ul className="ml-8 mb-6">
            {instructions.data.roleNames.map((role: any) => (
              <li className="list-disc mb-1 ml-4">
                <span className="font-semibold">{role}</span> <CopyButtonWithApproval value={role} />
              </li>
            ))}
          </ul>
          <li className="list-decimal mb-6 ml-4">
            Additional roles will need to be granted based on the GCP resources being accessed. Please review the{" "}
            <ExternalLink
              url={`${DOCS_URL}/reference/cloudaccount#gcp-details`}
              label="GCP Cloud Account Reference Page"
              disableCopy
            />{" "}
            for additional details
          </li>
          <li className="list-decimal mb-4 ml-4">
            Click <span className="font-semibold">Save</span>
          </li>
        </ul>
      ) : null}
      <div className="my-4">
        <button type={"button"} className="ngfocus color-link font-medium" onClick={onCPLN}>
          Configure the cloud account using the gcloud cli
          {continueWith === "cpln" ? (
            <ChevronUp className="ml-1 inline-block" size={16} style={{ transform: "translateY(2px)" }} />
          ) : (
            <ChevronDown className="ml-1 inline-block" size={16} style={{ transform: "translateY(2px)" }} />
          )}
        </button>
      </div>

      {continueWith === "cpln" ? (
        instructions.data.cli ? (
          <ul className="pl-8" style={{ maxWidth: 900 }}>
            <li className="list-decimal mb-2 ml-4">Install the Control Plane CLI.</li>
            <SyntaxHighlight code="npm i -g @controlplane/cli" language="bash" className="mb-6" />
            <li className="list-decimal mb-6 ml-4">
              Install the GCP CLI.
              <ExternalLink
                className="ml-2"
                url="https://cloud.google.com/sdk/docs/install"
                label="Get more information"
                disableCopy
              />
            </li>
            <li className="list-decimal mb-2 ml-4">
              Add the following policy bindings using the <span className="font-semibold">GCP CLI</span>.
            </li>
            <SyntaxHighlight
              code={instructions.data.cli
                .slice(1)
                .map((step: string) => step.replace("$PROJECT_ID", projectId))
                .join(" && \\\n")}
              language="bash"
              className="my-2"
            />

            <li className="list-decimal mt-6 mb-6 ml-4">
              Additional roles will need to be granted based on the GCP resources being accessed. Please review the
              <ExternalLink
                className="ml-1"
                url={`${DOCS_URL}/reference/cloudaccount#gcp-details`}
                label="GCP Cloud Account Reference Page"
                disableCopy
              />{" "}
              for additional details. Use the same gcloud command as the step above to add the applicable roles.
            </li>
            <li className="list-decimal mb-4 ml-4">You're done.</li>
          </ul>
        ) : null
      ) : null}
    </>
  );
};
